/**
 * Small pieces the Planner's two views share: the dialog, the segmented
 * switch, a remembered preference, and the form and button styles.
 */

export function loadPref<T extends string>(key: string, fallback: T): T {
  try { return (localStorage.getItem(key) as T) || fallback; } catch { return fallback; }
}
export function savePref(key: string, v: string) {
  try { localStorage.setItem(key, v); } catch { /* private mode */ }
}

export function Modal({ title, onClose, width, children }: { title: string; onClose: () => void; width: number; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 60,
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width, maxWidth: "100%", background: "var(--raised)", borderRadius: 14,
        padding: "22px 24px", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
      }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 500 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <div style={{ display: "inline-flex", gap: 3, padding: 3, background: "var(--tint)", borderRadius: 999 }}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          padding: "5px 12px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
          border: v === value ? "1px solid var(--accentLine)" : "1px solid transparent",
          background: v === value ? "var(--surface)" : "transparent",
          color: v === value ? "var(--accent)" : "var(--ink3)", fontWeight: v === value ? 500 : 400,
        }}>{l}</button>
      ))}
    </div>
  );
}

export const label: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)", marginBottom: 12 };
export const input: React.CSSProperties = {
  width: "100%", minWidth: 0, boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, fontSize: 13, marginTop: 4,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
export const rowInput: React.CSSProperties = { ...input, marginTop: 0 };
export const arrowBtn = (disabled: boolean): React.CSSProperties => ({
  width: 20, height: 20, padding: 0, borderRadius: 5, fontSize: 8, lineHeight: 1,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink3)",
  cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1,
});
export const primary: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none", whiteSpace: "nowrap",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
};
export const ghost: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 999, border: "1px solid var(--line2)", whiteSpace: "nowrap",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
export const on: React.CSSProperties = { background: "var(--accentTint)", border: "1px solid var(--accentLine)", color: "var(--accent)" };
export const iconBtn: React.CSSProperties = { border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", fontSize: 13 };
export const linkBtn: React.CSSProperties = { border: "none", background: "none", padding: 0, color: "var(--accent)", cursor: "pointer", fontSize: 12.5 };
